<script setup lang="ts">
/**
 * `ConfirmationView.vue` — port of `ConfirmationView.prefab` +
 * `ConfirmationViewModel.cs`.
 *
 * Prefab structure kept: a full-screen scrim at alpha 0.651 whose click fires
 * `OnUndecided`, a `DialogWithButtons` bound to
 * `HeaderText` / `ConfirmText` / `CancelText` / `WindowSize`, an
 * `IconTextToggle` "Don't show this again" (shown when
 * `IsDoNotShowAgainVisible`, i.e. a key was supplied), and a second
 * `IconTextToggle` carrying `AdditionalQuestion` / `AdditionalAnswer` (shown
 * when `IsAdditionalQuestionVisible`).
 *
 * `ConfirmationDarkView.vue` is the same view with the dark palette — Unity
 * ships two prefabs sharing this one ViewModel, so the web ships one component
 * with a `theme` prop and re-exports the dark spelling for name parity.
 *
 * Deviations (see `IConfirmationModel`): title + detail are separate elements
 * instead of one TMP rich-text string, and Esc maps to the same "undecided"
 * path as a backdrop click (Unity's desktop frame has no Esc binding, but a
 * web modal that traps Esc reads as broken).
 */
import { onBeforeUnmount, onMounted, ref } from "vue";
import DialogWithButtons from "@components/controls/DialogWithButtons.vue";
import Toggle from "@components/controls/Toggle.vue";
import type {
  ConfirmationArgs,
  ConfirmationResult,
} from "@models/notifications/IConfirmationModel";

defineOptions({ name: "ConfirmationView" });

const props = defineProps<{ args: ConfirmationArgs }>();
/**
 * `suppress` rides alongside the result rather than inside it: Unity's
 * `SaveDoNotShowAgain()` runs on confirm, cancel AND undecided, but the flag
 * is a view concern that must never reach the caller's `ConfirmationResult`.
 */
const emit = defineEmits<{
  (e: "answer", result: ConfirmationResult, suppress: boolean): void;
}>();

/** Unity `DoNotShowAgain` — the toggle's own state, saved on ANY answer. */
const doNotShowAgain = ref(false);
/** Unity `AdditionalAnswer`. */
const additionalAnswer = ref(false);

/**
 * One answer per dialog, and it matters more than it looks. The exit animation
 * keeps this component alive and clickable while `ConfirmationHost` has already
 * moved on to the next request — so a stray Escape or scrim click during those
 * milliseconds would reach the host and be applied to SOMEONE ELSE'S
 * confirmation. It also collapses the plain double-Escape case.
 */
let m_answered = false;

function answer(confirmed: boolean | undefined): void {
  if (m_answered) return;
  m_answered = true;
  emit(
    "answer",
    { confirmed, additionalAnswer: additionalAnswer.value },
    doNotShowAgain.value,
  );
}

/** Unity `OnConfirm`. */
function onConfirm(): void {
  answer(true);
}

/** Unity `OnCancel`. */
function onCancel(): void {
  answer(false);
}

/**
 * Unity `OnUndecided` (C3D-3208): clicking outside behaves like Cancel by
 * default, but a caller can opt into "navigate back and do nothing" —
 * signalled here by resolving `undefined`.
 */
function onUndecided(): void {
  answer(props.args.cancelIfUndecided === false ? undefined : false);
}

function onKey(e: KeyboardEvent): void {
  if (e.key === "Escape") onUndecided();
}

onMounted(() => window.addEventListener("keydown", onKey));
onBeforeUnmount(() => window.removeEventListener("keydown", onKey));
</script>

<template>
  <div
    class="cf-confirm__scrim cf-dialog-scrim"
    :class="{ 'cf-confirm__scrim--dark': args.theme === 'dark' }"
    @click="onUndecided"
  >
    <div class="cf-confirm__stack cf-dialog-layer" @click.stop>
      <DialogWithButtons
        :header-text="args.headerText"
        :detail-text="args.detailText"
        :confirm-text="args.confirmText"
        :cancel-text="args.cancelText"
        :width="args.width ?? 320"
        :height="args.height ?? 240"
        @confirm="onConfirm"
        @cancel="onCancel"
      >
        <label v-if="args.additionalQuestion" class="cf-confirm__toggle">
          <Toggle v-model="additionalAnswer" />
          <span>{{ args.additionalQuestion }}</span>
        </label>
      </DialogWithButtons>

      <label v-if="args.doNotShowAgainKey" class="cf-confirm__suppress">
        <Toggle v-model="doNotShowAgain" />
        <span>Don't show this again</span>
      </label>
    </div>
  </div>
</template>

<style scoped>
/*
 * The tint is handed to `.cf-dialog-scrim` as a custom property rather than
 * painted here: the shared recipe animates it on open and close, and adds the
 * viewport blur behind it (see `tokens.css`). The prefab values are unchanged.
 */
.cf-confirm__scrim {
  /* Prefab: alpha 0.6509804 over the default light backdrop. */
  --cf-dialog-scrim-tint: rgba(0, 0, 0, 0.651);
  position: fixed;
  inset: 0;
  z-index: 10500;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* `ConfirmationDarkView.prefab` scrim: rgb(0.180) @ 0.651. */
.cf-confirm__scrim--dark {
  --cf-dialog-scrim-tint: rgba(46, 46, 46, 0.651);
}

/* `position` / `z-index` come from `.cf-dialog-layer` — needed so this static
   stack paints above the scrim's positioned backdrop pseudo-element. */
.cf-confirm__stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--cf-space-4);
}

/* Prefab `IconTextToggle`: 130×30, font size 10, 50 % alpha label. */
.cf-confirm__suppress {
  display: flex;
  align-items: center;
  gap: var(--cf-space-3);
  height: 30px;
  font-size: 10px;
  color: var(--cf-color-text);
  opacity: 0.5;
  cursor: pointer;
}

.cf-confirm__toggle {
  display: flex;
  align-items: center;
  gap: var(--cf-space-3);
  font-size: var(--cf-font-size-sm);
  color: var(--cf-color-text);
  cursor: pointer;
}
</style>
